import React, { forwardRef, ReactNode, RefObject } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import colors from '../../common/theme/colors';
import constants, { border } from '../../common/theme/constants';

export interface DropDownItemEventObject {
  value: string;
  label: string;
  onSelect?: () => void;
}

export type DropDownItemProps = {
  label?: string;
  value?: string;
  onClick?: (
    EventObject: DropDownItemEventObject,
    MouseEvent: React.MouseEvent<HTMLButtonElement>
  ) => void;
  active?: boolean;
  target?: string;
  href?: string;
  children?: string | ReactNode;
  onSelect?: () => void;
  ref?: RefObject<HTMLButtonElement>;
};

const MenuButton = styled.button`
  font-family: ${({ theme }) => theme.font.fontFamily};
  box-sizing: border-box;
  display: block;
  width: 100%;
  padding: ${constants.spacing.absolute.s}px ${constants.spacing.absolute.m}px;
  font-weight: ${constants.fontWeight.normal};
  text-align: inherit;
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  font-size: ${constants.fontSize.p}rem;
  color: ${({ theme }) => theme.page.body.text};
  text-decoration: none;

  &.active,
  &:active {
    background-color: ${colors.gray_30};
    color: ${({ theme }) => theme.page.body.text};
  }

  &:hover {
    background-color: ${({ theme }) => theme.button.link.hover.bg};
  }

  &:focus {
    box-shadow: 0 0 0 ${border.width}px ${({ theme }) => theme.focus};
  }
`;

const MenuItem = styled.li`
  list-style-type: none;
  min-width: 125px;
`;

const KitDropDownItem = forwardRef(
  (props: DropDownItemProps, ref: React.RefObject<HTMLButtonElement>) => {
    const { value, label, active, onClick, children, href, target, onSelect } = props;
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (onClick) {
        onClick({ value, label, onSelect } as DropDownItemEventObject, event);
      }
    };

    const extendedMenuButtonProps: {
      as?: 'a' | 'button';
      href?: string;
      onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
      target?: string;
    } = {
      as: href ? 'a' : 'button',
      href: href && href,
      onClick: onSelect,
      target: (href && target && target) || undefined,
    };
    extendedMenuButtonProps.onClick = href ? undefined : handleClick;
    return (
      <MenuItem>
        <MenuButton
          className={classNames(active && 'active')}
          aria-label={label}
          role='button'
          ref={ref}
          {...extendedMenuButtonProps}
        >
          {children || label}
        </MenuButton>
      </MenuItem>
    );
  }
);

export default KitDropDownItem;
